import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { selectSystemBack } from '../platform/system-back';
import { decideSystemBack, readRouterHistoryIndex } from './system-back-policy';

/**
 * The system Back gesture, joined to the app's own history — M10 / S10-03B.
 *
 * ONE SMALL CENTRAL HANDLER, NOT A SPECIAL CASE. Jerry's authorization is
 * explicit that the report photo viewer must not be special-cased if a central
 * handler can honour the history the app already expresses — and it can,
 * because every screen in Crewlink navigates through the router. So Back means
 * the same thing everywhere: Viewer → Preview → Photo selection → Capture
 * selection, Capture → Project, Settings → wherever Settings was opened from.
 * No route learns that it is being navigated away from.
 *
 * WHY MOUNTING IT HERE PRESERVES THE IN-PROGRESS REPORT. It navigates; it never
 * unmounts anything itself. `ReportBuilderLayout` stays mounted across its own
 * steps because they are its CHILD routes (`report-route.test.tsx` enforces
 * that), so stepping back through them keeps the selected Captures, the chosen
 * photos, the Description and the branding choice exactly where they were.
 * **Nothing here is persisted, and nothing here needs to be:** durable drafts
 * across a real process death are S10-05, not this.
 *
 * IT RENDERS NOTHING. It is a behaviour, not a screen; there is no wrapper
 * element to disturb layout and nothing for the design export to describe.
 *
 * WHY IT IS A COMPONENT RATHER THAN A CALL IN `main.tsx`: it needs the router's
 * `navigate`, and it must stop listening if the tree is ever torn down. A
 * listener that outlived its navigator would pop history entries on behalf of a
 * router that no longer exists.
 */
export function SystemBackBridge(): null {
  const navigate = useNavigate();
  // Selected once, like every other port (ARCHITECTURE §3) — and once per
  // mounted bridge, of which there is exactly one.
  const [systemBack] = useState(() => selectSystemBack());

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let torndown = false;

    void systemBack
      .subscribe((event) => {
        const decision = decideSystemBack({
          // `window.history` is the router's own record of where it is, not a
          // parallel stack: React Router stamps `idx` into it on every push.
          routerIndex: readRouterHistoryIndex(
            typeof window === 'undefined' ? null : window.history,
          ),
          canGoBack: event.canGoBack,
        });

        if (decision === 'history-back') {
          // Exactly one entry. Never a route name, never a computed
          // destination — a Back that navigates somewhere of its own choosing
          // is how stale routes and loops are built.
          void navigate(-1);
          return;
        }

        // The root. Leaving is the correct Android behaviour, and it is also
        // the only thing that keeps Back from becoming a dead key: the plugin
        // does nothing at all here when no listener answers.
        void systemBack.exitApp();
      })
      .then((remove) => {
        if (torndown) {
          remove();
          return;
        }
        unsubscribe = remove;
      });

    return () => {
      torndown = true;
      unsubscribe?.();
    };
  }, [navigate, systemBack]);

  return null;
}
